import { memo, useEffect, useMemo, useRef } from 'react';
import { Plane, Vector2, Vector2Tuple, Vector3 } from 'three';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { useDollhouse } from '@spazious/dollhouse-engine';
import { FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';
import { useStaging, useStore } from '@spazious/dollhouse-store';
import { MeasurementStackEnum } from '@spazious/dollhouse-types';
import { useEventCallback } from '@spazious/react-hooks';

export const MeasurementDrawControls: React.FC = memo(() => {
  const selectorRef = useRef<string | null>(null);
  const { measurements, set } = useStore();
  const { staging, createAction } = useStaging();

  const camera = useDollhouse(({ camera }) => camera);
  const pointer = useDollhouse(({ pointer }) => pointer);
  const raycaster = useDollhouse(({ raycaster }) => raycaster);
  const events = useDollhouse(({ events }) => events);
  const plane = useMemo(() => new Plane(camera.up, 0), [camera.up]);

  const points = useRef<Vector2Tuple[]>([]);

  const getPoint = useEventCallback(() => {
    const point = new Vector3();
    raycaster.setFromCamera(pointer, camera);
    raycaster.ray.intersectPlane(plane, point);
    return [point.x, point.z] as [number, number];
  });

  const elem = events.connected as HTMLElement | undefined;

  const getDistance = (a: Vector2, b: Vector2): number => {
    return a.distanceTo(b);
  };

  useEffect(() => {
    if (measurements.stack === MeasurementStackEnum.Done) {
      if (selectorRef.current && staging.stack.length && points.current.length > 1) {
        staging.add(
          createAction<[number, number][]>('update', selectorRef.current, 'points', [
            ...points.current,
          ])
        );
        staging.commit(CommanderActionTypesEnum.Add);
        set({
          measurements: { ...measurements, drawEnabled: false },
        });
      }
    } else if (measurements.stack === MeasurementStackEnum.Remove) {
      set({
        measurements: { drawEnabled: false, stack: MeasurementStackEnum.Initial },
      });
      staging.abort();
    }
  }, [measurements]);

  useEffect(() => {
    const handlePointerDown = () => {
      if (selectorRef.current) {
        const _va = new Vector2();
        const _vb = new Vector2();
        const lastPoint = points.current[points.current.length - 1];
        const currentPoint = getPoint();
        const distance = getDistance(_va.fromArray(lastPoint), _vb.fromArray(currentPoint));
        if (distance > 0.1) {
          set({
            measurements: { ...measurements, stack: MeasurementStackEnum.MultiplePoint },
          });
          points.current.push(getPoint());
          staging.add(
            createAction<[number, number][]>('update', selectorRef.current, 'points', [
              ...points.current,
            ])
          );
        }
      } else {
        const action = createAction('add', { type: FShallowNodeTypesEnum.Measurement });
        selectorRef.current = action.payload.selector;
        staging.add(action);
        points.current.push(getPoint());
        staging.add(
          createAction<[number, number][]>('update', selectorRef.current, 'points', [
            ...points.current,
          ])
        );
      }
    };

    const handlePointerMove = () => {
      if (selectorRef.current) {
        staging.add(
          createAction<[number, number][]>('update', selectorRef.current, 'points', [
            ...points.current,
            getPoint(),
          ])
        );
      }
    };

    elem?.addEventListener('pointermove', handlePointerMove);
    elem?.addEventListener('pointerdown', handlePointerDown);

    return () => {
      elem?.removeEventListener('pointerdown', handlePointerDown);
      elem?.removeEventListener('pointermove', handlePointerMove);
      staging.abort();
    };
  }, [createAction, elem, getPoint, staging]);

  return null;
});
